import { FC, useMemo, useState } from 'react';
import { FaIcon } from 'fe-learner-common';
import { useIntl } from 'react-intl';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { EmptyStateWrapper } from '@fe-learner-common/components/EmptyState';
import { TPerson } from '@api/io/session.io';
import { Person } from '@components/Person';
import { ContextPagination as Pagination } from '@components/ContextPagination';
import { usePageParam } from '@hooks/params/usePageParam';
import { TabPage } from '@routes/session-details/components/TabPage';
import { PaginationPageSize } from '@fe-core-ui/src/components/Pagination/PageSizeSelect';
type ParticipantsTab = {
  persons?: TPerson[] | null;
};

export const ParticipantsTab: FC<ParticipantsTab> = ({ persons }) => {
  const intl = useIntl();
  const { page, setPage } = usePageParam();
  const pageIndex = page - 1;
  const [pageSize, setPageSize] = useState<PaginationPageSize>(16);

  const paginatedPersons = useMemo(() => {
    if (!persons || !persons?.length) {
      return [];
    }

    const calculatedPageSize = pageSize === 'all' ? persons.length : pageSize;
    return persons?.slice(
      pageIndex * calculatedPageSize,
      (pageIndex + 1) * calculatedPageSize,
    );
  }, [persons, pageIndex, pageSize]);

  return (
    <TabPage
      title={intl.formatMessage({ id: 'catalog.tab.participants.title' })}
    >
      <div className="lf:flex lf:flex-col lf:gap-y-4">
        <EmptyStateWrapper
          icon={<FaIcon icon={faUser} className="lf:text-neutral-550" />}
          label={intl.formatMessage({
            id: 'courses.tab.participants.empty.label',
          })}
          description={intl.formatMessage({
            id: 'courses.tab.participants.empty.description',
          })}
          condition={!!persons?.length}
        >
          <div className="lf:grid lf:grid-cols-1  lf:items-center lf:justify-center lf:gap-4 lf:md:grid-cols-2 lf:xl:grid-cols-3">
            {paginatedPersons?.map((person) => (
              <Person key={`participant-list-${person.id} `} person={person} />
            ))}
          </div>
        </EmptyStateWrapper>
        <Pagination
          type="link"
          actionContext="Instructors"
          className="lf:px-4 lf:py-3"
          page={page}
          totalNoItems={persons?.length || 0}
          pageSizeOptions={{
            pageSize,
            onPageSizeChange: (pageSize) => {
              setPageSize(pageSize);
            },
          }}
          setPageParams={(page) => {
            setPage(page.toString());
          }}
          texts={{
            ariaLabel: intl.formatMessage({
              id: 'courses.tab.instructors.pagination.ariaLabel',
            }),
          }}
        />
      </div>
    </TabPage>
  );
};
